import { useEffect, useState } from 'react';
import { API_BASE } from '../api/client';
import { devGetMe } from '../api/dev';
import Spinner from '../components/Spinner';
import '../styles.css';

export default function DevChangePassword() {
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [loading, setLoading] = useState(false);
  const [tenantInfo, setTenantInfo] = useState<{ id?: string; name?: string }>({});
  const [showForm, setShowForm] = useState(false);
  const [mustChange, setMustChange] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [showCurr, setShowCurr] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConf, setShowConf] = useState(false);

  function passwordStrength(p: string) {
    const lenOk = (p || '').length >= 10; // stronger than backend minimum
    const lower = /[a-z]/.test(p);
    const upper = /[A-Z]/.test(p);
    const digit = /\d/.test(p);
    const special = /[^a-zA-Z0-9]/.test(p);
    const classes = [lower, upper, digit, special].filter(Boolean).length;
    const banned = /(password|12345|qwerty)/i.test(p);
    const strong = lenOk && classes >= 3 && !banned;
    return { lenOk, lower, upper, digit, special, classes, banned, strong };
  }

  useEffect(() => {
    (async () => {
      try { const me = await devGetMe(); setTenantInfo({ id: me.tenantId, name: me.name || '' }); } catch {}
      try {
        const t = localStorage.getItem('afw.dev.token') || '';
        if (t && t.split('.').length >= 2) {
          const payload = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
          if (payload && payload.mustChange) { setMustChange(true); setShowForm(true); }
        }
      } catch {}
      setInitLoading(false);
    })();
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault(); setError(''); setOk(''); setLoading(true);
    try {
      if (newPassword !== confirm) throw new Error('passwords do not match');
      if (newPassword === currentPassword) throw new Error('new password must be different from current password');
      const s = passwordStrength(newPassword);
      if (!s.strong) throw new Error('password is not strong enough');
      const token = localStorage.getItem('afw.dev.token') || '';
      const r = await fetch(`${API_BASE}/dev/password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Authorization': `Bearer ${token}` },
        body: JSON.stringify({ currentPassword, newPassword })
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error?.message || `HTTP ${r.status}`);
      if (j?.token) localStorage.setItem('afw.dev.token', j.token);
      setOk('Password changed.');
      window.location.href = '/portal/health';
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally { setLoading(false); }
  }

  const passwordsMismatch = !!newPassword && !!confirm && newPassword !== confirm;

  if (initLoading) {
    return (
      <div>
        <h2>Security</h2>
        <div className="content" style={{ display: 'grid', placeItems: 'center', paddingTop: 24 }}>
          <Spinner label="Loading..." />
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2>Security</h2>

      <div className="card" style={{ padding: 16, marginBottom: 12 }}>
        <div className="muted">Tenant</div>
        <div>{tenantInfo.name || '-'}</div>
        <div className="muted" style={{ fontSize: 12 }}>Tenant ID: <code>{tenantInfo.id || ''}</code></div>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div className="muted">Password</div>
          {!showForm && (
            <button className="btn" onClick={() => { setShowForm(true); setError(''); setOk(''); }}>Change Password</button>
          )}
        </div>
        {mustChange && (
          <div className="muted" style={{ marginBottom: 8 }}>
            Your password must be changed before proceeding. Enter your current password and choose a new one.
          </div>
        )}
        {showForm && (
          <form onSubmit={submit}>
            <div style={{ maxWidth: 420 }}>
              <div style={{ position: 'relative', marginBottom: 10 }}>
                <input
                  className="txt"
                  type={showCurr ? 'text' : 'password'}
                  placeholder="Current password"
                  value={currentPassword}
                  onChange={e => setCurrent(e.target.value)}
                  style={{ width: '100%' }}
                  autoFocus
                />
                <button type="button" className="btn secondary" onClick={() => setShowCurr(s => !s)} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', padding: 4, lineHeight: 0, minWidth: 0 }} aria-label={showCurr ? 'Hide password' : 'Show password'} title={showCurr ? 'Hide password' : 'Show password'}>
                  {showCurr ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a20.76 20.76 0 0 1 5.06-6.14M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a20.77 20.77 0 0 1-3.06 4.22"/>
                      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
              <div style={{ position: 'relative', marginBottom: 6 }}>
                <input
                  className="txt"
                  type={showNew ? 'text' : 'password'}
                  placeholder="New password"
                  value={newPassword}
                  onChange={e => setNew(e.target.value)}
                  style={{ width: '100%' }}
                />
                <button type="button" className="btn secondary" onClick={() => setShowNew(s => !s)} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', padding: 4, lineHeight: 0, minWidth: 0 }} aria-label={showNew ? 'Hide password' : 'Show password'} title={showNew ? 'Hide password' : 'Show password'}>
                  {showNew ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a20.76 20.76 0 0 1 5.06-6.14M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a20.77 20.77 0 0 1-3.06 4.22"/>
                      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
              {newPassword && (() => { const s = passwordStrength(newPassword); return (
                <div style={{ fontSize: 12, marginBottom: 8 }}>
                  <div className="muted" style={{ marginBottom: 4 }}>Password must meet all:</div>
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    <li style={{ color: s.lenOk ? 'lightgreen' : 'orange' }}>At least 10 characters</li>
                    <li style={{ color: s.classes >= 3 ? 'lightgreen' : 'orange' }}>Include 3 of: lowercase, uppercase, number, symbol</li>
                    <li style={{ color: (newPassword !== currentPassword && !!currentPassword) ? 'lightgreen' : 'orange' }}>Different from current password</li>
                  </ul>
                </div>
              ); })()}
              <div style={{ position: 'relative', marginBottom: 10 }}>
                <input
                  className="txt"
                  type={showConf ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  style={{ width: '100%' }}
                />
                <button type="button" className="btn secondary" onClick={() => setShowConf(s => !s)} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', padding: 4, lineHeight: 0, minWidth: 0 }} aria-label={showConf ? 'Hide password' : 'Show password'} title={showConf ? 'Hide password' : 'Show password'}>
                  {showConf ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a20.76 20.76 0 0 1 5.06-6.14M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a20.77 20.77 0 0 1-3.06 4.22"/>
                      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            {passwordsMismatch && (
              <div style={{ color: 'crimson', marginBottom: 6 }}>Passwords do not match</div>
            )}
            {error && <div style={{ color: 'crimson', marginBottom: 8 }}>Error: {error}</div>}
            {ok && <div style={{ color: 'lightgreen', marginBottom: 8 }}>{ok}</div>}
            <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn secondary"
                onClick={() => { setShowForm(false); setCurrent(''); setNew(''); setConfirm(''); setError(''); setOk(''); }}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="btn success"
                disabled={(() => { const s = passwordStrength(newPassword); return loading || !currentPassword || !newPassword || !confirm || passwordsMismatch || !s.strong || newPassword === currentPassword; })()}
              >
                Save Password
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
